import * as React from "react";
import { DataLayer, ProductsSd } from "@grandvisionhq/analytics";
import { useLocation } from "@grandvisionhq/frk-routing";
import { ProductListing } from "@grandvisionhq/products";
import { useIntl } from "@grandvisionhq/state";
import { useDispatchTrackEvent } from "@grandvisionhq/svc-tealium/events";
import { isArray } from "@grandvisionhq/utils";
import { getForwardedQueryParamsFromFacets } from "../../Algolia/utils/Algolia/utils/get-forwarded-query-params-from-facets.js";
import { useFacets } from "../../hooks/hooks/use-facets.js";
import { ConnectedInfinitePagination } from "../../InfinitePagination/InfinitePagination/InfinitePagination.js";
import { NoResults } from "../../NoResults/NoResults/NoResults.js";
import { mapFacetsToTealiumFacetValue } from "../../Tealium/mappers/Tealium/mappers/map-facets-to-tealium-facet-value.js";
import { createPagedHits } from "./create-paged-hits.js";
import { useFormatProductLinks } from "../hooks/ConnectedProductListing/hooks/use-format-product-links.js";
import { useAlgoliaConfig } from "../../Algolia/Algolia/AlgoliaConfig.js";
import { useAlgoliaData } from "../../Algolia/hooks/Algolia/hooks/use-algolia-data.js";
function ConnectedProductListing({
  promotionBig,
  promotionSmall,
  reimbursableLogos
}) {
  const { getLabel } = useIntl();
  const { pageSize, facets, indexName } = useAlgoliaConfig();
  const { mappedResults: hits, total, searchState, isLoading, searchQuery } = useAlgoliaData();
  const { query, configure, refinementList } = searchState;
  const page = configure?.page || 1;
  const length = configure?.length ? Number(configure.length) : page * pageSize;
  const { pathname } = useLocation();
  const dispatchTrackEvent = useDispatchTrackEvent();
  const { appliedFacets, numberOfAppliedFacets } = useFacets();
  const previousNumberofAppliedFacets = React.useRef(numberOfAppliedFacets);
  const facetsWithOptionalSearchTerm = pathname.startsWith("/search") ? { ...appliedFacets, q: { value: [searchQuery], label: "" } } : appliedFacets;
  React.useEffect(() => {
    if (previousNumberofAppliedFacets.current !== numberOfAppliedFacets && !isLoading) {
      previousNumberofAppliedFacets.current = numberOfAppliedFacets;
      dispatchTrackEvent(
        () => Promise.resolve({
          id: "Event",
          Events_SearchFiltering: "1",
          Search_ResultItemsQnt: `${total}`,
          Search_FacetValues_String: mapFacetsToTealiumFacetValue(facetsWithOptionalSearchTerm)
        })
      );
    }
  }, [
    previousNumberofAppliedFacets,
    dispatchTrackEvent,
    appliedFacets,
    total,
    isLoading,
    numberOfAppliedFacets
  ]);
  React.useEffect(() => {
    const filterKeys = Object.keys(facetsWithOptionalSearchTerm);
    const combinedFilterValues = Object.values(facetsWithOptionalSearchTerm).map(
      (facet) => isArray(facet.value) ? facet.value.join("-") : facet.value
    );
    DataLayer.pushEvent({
      event: "page_data_loaded",
      page: {
        unixTimestamp: Math.floor(Date.now() / 1e3),
        type: "product listing",
        filter_type: filterKeys.join(", "),
        filter_value: combinedFilterValues.join(", ")
      }
    });
  }, []);
  const forwardedQueryParams = getForwardedQueryParamsFromFacets(facets, refinementList);
  const pages = React.useMemo(() => {
    const totalNumberOfPages = Math.ceil(total / pageSize);
    const fill = length > hits.length && hits.length !== total ? Math.min(totalNumberOfPages, page) - 1 : 0;
    if (page > totalNumberOfPages) {
      console.log(
        `[PLP] The requested page ${page} exceeds the total number of pages (Total number of pages: ${totalNumberOfPages}).`
      );
    }
    return createPagedHits(hits, total, pageSize, fill);
  }, [hits, total, pageSize, page, length]);
  const links = useFormatProductLinks({
    products: hits
  });
  if (hits.length === 0) {
    return /* @__PURE__ */ React.createElement(
      NoResults,
      {
        title: getLabel("search.noResults.title"),
        description: getLabel("search.noResults.text"),
        query
      }
    );
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    ProductListing,
    {
      products: pages,
      showMoreButton: /* @__PURE__ */ React.createElement(ConnectedInfinitePagination, null),
      promotionBig,
      promotionSmall,
      reimbursableLogos,
      forwardedQueryParams,
      indexName
    }
  ), /* @__PURE__ */ React.createElement(ProductsSd, { items: hits, links }));
}
export {
  ConnectedProductListing
};
//# sourceMappingURL=ConnectedProductListing.js.map
